/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { persistOptions } from '@/lib/pwa/persistOptions';
import { BrowserRouter } from 'react-router-dom';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { ThemeProvider } from '@/lib/theme';
import { projectConfig } from '@/config/project.config';
import { Toaster, ContextMenuRoot } from '@/components/ui';
import { PwaStatus } from '@/components/pwa/PwaStatus';
import { useAuthStore } from '@/stores/authStore';
import { clearPersistedQueryCache } from '@/lib/pwa/persister';
import App from './App';
import './styles/globals.css';

// Initialize i18n
import '@/lib/i18n';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 1000 * 60 * 60 * 24, // 24h — keep long enough for the persister
      retry: 1,
    },
  },
});

async function bootstrap() {
  // Shared-device safety: if we're booting without an authenticated session
  // (logged out, or relaunched after auth was cleared), drop any persisted
  // reference cache before PersistQueryClientProvider can restore it. Closes the
  // force-close/crash gap in the login/logout clear contract.
  if (!useAuthStore.getState().isAuthenticated) {
    await clearPersistedQueryCache();
  }
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <ThemeProvider defaultTheme={projectConfig.theme.default}>
          <TooltipPrimitive.Provider delayDuration={400}>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ContextMenuRoot>
                <App />
                <PwaStatus />
                <Toaster />
              </ContextMenuRoot>
            </BrowserRouter>
          </TooltipPrimitive.Provider>
        </ThemeProvider>
      </PersistQueryClientProvider>
    </React.StrictMode>
  );
}
void bootstrap();
